/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.scss'
import { togglePopup } from '../../../Redux/popups/popupSlice'
import { thisWeekGenerator, arrayOfDays } from '../Appointments/timeLines'
import AptMiniCard from '../Appointments/AptMiniCard'
import { Area, AreaChart, Bar, BarChart, Label, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useTranslation } from 'react-i18next'

export default function index() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { appointments } = useSelector((state) => state.appointments)
  const auth = useSelector((state) => state.auth)
  const { profile, user } = auth.user || {}
  const isAdmin = profile?.role === 'admin'

  // Add logging to see all appointments and their categories
  console.log('All appointments:', appointments?.map(apt => ({
    id: apt.id,
    category: apt.category,
    date: apt.date,
    patient_name: apt.patient_name
  })))

  const usedName = 'Dr. ' + (profile?.displayName || user?.displayName || 'No name')

  // Get this week's dates and appointments
  const thisWeekDatesApts = thisWeekGenerator()?.map((date) => {
    return {
      ...date,
      apts: appointments?.filter((appointment) => 
        appointment?.date === date.fullDate && 
        !appointment?.isDeletedFromCalendar && 
        appointment?.category !== 'pulmonology'
      ) || [],
      patientIds:
        appointments?.filter((appointment) => 
          appointment?.date === date.fullDate && 
          !appointment?.isDeletedFromCalendar && 
          appointment?.category !== 'pulmonology'
        )?.map((appointment) => appointment.patient_id) || []
    }
  })
  const todaysData = thisWeekDatesApts?.find((date) => date.isToday) || {}
  
  // Add logging to see today's appointments and their categories
  console.log('Today\'s appointments:', todaysData?.apts?.map(apt => ({
    id: apt.id,
    category: apt.category,
    patient_name: apt.patient_name
  })))

  // Function to group appointments by patient, date, and time to show multiple services as one appointment
  const groupAppointmentsByPatientDateTime = (appointments) => {
    const groupMap = new Map()
    
    appointments.forEach(appointment => {
      const key = `${appointment.patient_id}-${appointment.date}-${appointment.fromStr}-${appointment.toStr}`
      
      if (!groupMap.has(key)) {
        // Create new group with the first appointment as base
        groupMap.set(key, {
          ...appointment,
          services: appointment.services || [appointment.operation_name].filter(Boolean),
          totalPrice: appointment.price || 0,
          serviceNames: [appointment.operation_name].filter(Boolean),
          appointmentCount: 1,
          groupId: `group-${appointment.patient_id}-${appointment.date}-${appointment.fromStr}` // Unique group ID
        })
      } else {
        // Add to existing group
        const existingGroup = groupMap.get(key)
        existingGroup.totalPrice += (appointment.price || 0)
        existingGroup.appointmentCount += 1
        
        // Add service/operation name if it's different and not already included
        if (appointment.operation_name && !existingGroup.serviceNames.includes(appointment.operation_name)) {
          existingGroup.serviceNames.push(appointment.operation_name)
        }
        
        // Add to services array if it exists
        if (appointment.services) {
          existingGroup.services = [...new Set([...existingGroup.services, ...appointment.services])]
        }
        
        // Update the price to reflect the total
        existingGroup.price = existingGroup.totalPrice
      }
    })
    
    return Array.from(groupMap.values())
  }

  // Group today's appointments by patient, date, and time
  const groupedTodaysApts = groupAppointmentsByPatientDateTime(todaysData?.apts || [])
  
  // Sort appointments by time (earliest to latest)
  // This ensures appointments are displayed in chronological order throughout the day
  const sortedGroupedApts = groupedTodaysApts.sort((a, b) => {
    // First sort by 'from' time (start time) - earlier appointments first
    if (a.from !== b.from) {
      return a.from - b.from
    }
    // If start times are the same, sort by 'to' time (end time) - shorter appointments first
    return a.to - b.to
  })
  
  const expectedIncomeToday = sortedGroupedApts?.reduce((acc, appointment) => acc + (appointment?.totalPrice || appointment?.price || 0), 0)

  const { apts } = { apts: sortedGroupedApts }

  const processData = (data, dates) => {
    return dates.map((date, index) => {
      // Get all appointments for this date first
      const dateAppointments = data.filter((item) => 
        item.date === date.fullDate && 
        !item.isDeletedFromCalendar && 
        item.category !== 'pulmonology'
      )
      // Group them by patient, date, and time to get unique appointments
      const groupedDateAppointments = groupAppointmentsByPatientDateTime(dateAppointments)
      const appointmentsNum = groupedDateAppointments.length || 0
      return {
        date: date.day,
        appointments: appointmentsNum
      }
    })
  }

  const chartData = processData(appointments, thisWeekDatesApts)

  return (
    <div className={styles.container}>
      <div className={styles.upperdata_header}>
        <div className={styles.dr_greeting_container}>
          <h2 className={styles.dr_greeting_text}>
            {t('goodDay')}, <span className={styles.dr_greeting_name}>{usedName}</span>
          </h2>
          <p className={styles.patient_info}>{t('patients_today', { count: sortedGroupedApts?.length || 0 })}</p>
        </div>
      </div>
      <div className={styles.bottom_info}>
        <div className={styles.apt_show_case_container}>
          <div className={styles.apt_show_case_header}>
            <span className={styles.apt_icon} role="img" aria-label="calendar">📅</span>
            <div>
              <h2 className={styles.apt_show_case_title}>{t('td_apts')}</h2>
              <p className={styles.apt_show_case_subtitle}>{t('today_summary')}</p>
            </div>
            <span className={styles.apt_count_badge}>{sortedGroupedApts?.length}</span>
          </div>
          <div className={styles.apt_show_case_cards}>
            {apts?.length ? (
              apts?.map((appointment) => {
                const { id, groupId, patient_name, fromStr, doctor_name, serviceNames, appointmentCount, totalPrice } = appointment
                return (
                  <div key={groupId || id} className={styles.apt_card}>
                    <div className={styles.apt_card_header}>
                      <span className={styles.apt_card_patient}>{patient_name || t('no_name')}</span>
                      <span className={styles.apt_card_time}>{fromStr || '--:--'}</span>
                    </div>
                    <div className={styles.apt_card_category}>
                      {doctor_name || t('no_doctor')}
                    </div>
                    {appointmentCount > 1 && (
                      <div className={styles.apt_card_services}>
                        <span className={styles.services_count}>
                          {appointmentCount} {t('services')}{serviceNames?.length > 0 && ': '}
                          {serviceNames?.join(', ') || ''}
                        </span>
                      </div>
                    )}
                    {serviceNames && serviceNames.length === 1 && (
                      <div className={styles.apt_card_service}>
                        <span className={styles.single_service}>{serviceNames[0]}</span>
                      </div>
                    )}
                  </div>
                )
              })
            ) : (
              <div className={styles.no_apts}>
                <p className={styles.txt}>{t('no_apts')}</p>
              </div>
            )}
          </div>
        </div>
        <div className={styles.pt_show_case_container}>
          <h2 className={styles.pt_show_case_title}>{t('total_pts_this_week')}</h2>
          <ResponsiveContainer width='100%' height='60%'>
            <BarChart data={chartData}>
              <XAxis dataKey='date' />
              <Tooltip
                cursor={{
                  fill: 'transparent',
                  labelStyle: {
                    color: 'black',
                    backgroundColor: 'white',
                    border: '1px solid black'
                  }
                }}
              />
              <Bar
                dataKey='appointments'
                fill='#D7EAEA'
                label={{
                  position: 'center',
                  fill: 'black',
                  color: 'black',
                  fontSize: 12,
                  fontWeight: 'bold',
                  display: 'insideEnd'
                }}
              ></Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
